/** 视口内等比缩放容器，与 Keyboard 内层逻辑一致，可包裹多块 UI（如键盘+侧栏）整体缩放 */

import React, { useState, useCallback, useRef, useLayoutEffect } from "react";
import "./Keyboard.css";

const DEFAULT_FIT_PADDING = 12;
const DEFAULT_MAX_SCALE = 4;

export interface ViewportFitProps {
  children: React.ReactNode;
  className?: string;
  /** 参与缩小计算的可视区内边距（px） */
  fitPadding?: number;
  /** 相对自然尺寸的最大放大倍数 */
  maxScale?: number;
}

export const ViewportFit: React.FC<ViewportFitProps> = ({
  children,
  className,
  fitPadding = DEFAULT_FIT_PADDING,
  maxScale = DEFAULT_MAX_SCALE,
}) => {
  const fitContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  /** 仅首次内在尺寸与 scale 就绪前隐藏，避免用户看到错误缩放；之后 naturalSize 微变不再隐藏，只 recalcScale */
  const [paintReady, setPaintReady] = useState(false);
  const firstRevealDoneRef = useRef(false);
  /** 上次用于「首次展示」的尺寸；内容从小错误突然变为正确大小时需重新走隐藏→缩放→展示，避免偶发超大缩放 */
  const lastRevealNaturalRef = useRef({ w: 0, h: 0 });

  const updateNaturalSize = useCallback(() => {
    const el = measureRef.current;
    if (!el) return;
    const w = Math.max(el.scrollWidth, el.offsetWidth);
    const h = Math.max(el.scrollHeight, el.offsetHeight);
    setNaturalSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, []);

  const recalcScale = useCallback(() => {
    const fit = fitContainerRef.current;
    if (!fit) return;
    const { w: iw, h: ih } = naturalSize;
    if (iw < 8 || ih < 8) return;
    const pad = fitPadding;
    const cw = Math.max(0, fit.clientWidth - pad);
    const ch = Math.max(0, fit.clientHeight - pad);
    if (cw < 8 || ch < 8) return;
    const s = Math.min(maxScale, cw / iw, ch / ih);
    const clamped = Math.max(0.18, s);
    setScale((prev) => (Math.abs(prev - clamped) > 0.003 ? clamped : prev));
  }, [naturalSize, fitPadding, maxScale]);

  useLayoutEffect(() => {
    updateNaturalSize();
    const el = measureRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateNaturalSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateNaturalSize]);

  useLayoutEffect(() => {
    const run = () => {
      recalcScale();
      requestAnimationFrame(() => recalcScale());
    };
    run();
    const fit = fitContainerRef.current;
    if (!fit) return;
    const ro = new ResizeObserver(() => recalcScale());
    ro.observe(fit);
    window.addEventListener("resize", recalcScale);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalcScale);
    };
  }, [recalcScale, naturalSize]);

  useLayoutEffect(() => {
    const w = naturalSize.w;
    const h = naturalSize.h;
    if (w < 8 || h < 8) {
      setPaintReady(false);
      firstRevealDoneRef.current = false;
      lastRevealNaturalRef.current = { w: 0, h: 0 };
      return;
    }

    const prev = lastRevealNaturalRef.current;
    const contentGrewSubstantially =
      prev.w >= 8 &&
      prev.h >= 8 &&
      firstRevealDoneRef.current &&
      (w > prev.w * 1.38 || h > prev.h * 1.38);

    if (contentGrewSubstantially) {
      setPaintReady(false);
      firstRevealDoneRef.current = false;
    }

    if (firstRevealDoneRef.current) {
      lastRevealNaturalRef.current = { w, h };
      recalcScale();
      return;
    }
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      recalcScale();
      raf2 = requestAnimationFrame(() => {
        recalcScale();
        if (!cancelled) {
          setPaintReady(true);
          firstRevealDoneRef.current = true;
          lastRevealNaturalRef.current = { w, h };
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [naturalSize.w, naturalSize.h, recalcScale]);

  return (
    <div
      ref={fitContainerRef}
      className={`shared-keyboard-fit-root ${className ?? ""}`.trim()}
    >
      <div
        className="shared-keyboard-fit-slot"
        style={
          naturalSize.w > 0
            ? {
                width: naturalSize.w * scale,
                height: naturalSize.h * scale,
              }
            : { minHeight: 200 }
        }
      >
        <div
          className="shared-keyboard-fit-scaler"
          style={{
            position: "absolute",
            left: "50%",
            top: 0,
            transform: `translateX(-50%) scale(${scale})`,
            transformOrigin: "top center",
            opacity: paintReady ? 1 : 0,
            visibility: paintReady ? "visible" : "hidden",
            pointerEvents: paintReady ? "auto" : "none",
            transition: paintReady ? "opacity 0.08s ease-out" : "none",
          }}
        >
          <div ref={measureRef} className="shared-keyboard-fit-measure">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
